import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { USER_ROLES, userHasRole, type AppUser, type UserRole } from '@/lib/auth';
import { signInWithPassword, type SignInResult } from '@/lib/services/auth';
import { supabase } from '@/lib/supabase/client';
import logo from '@/assets/logo.jpg';

const DASHBOARD_PATH = '/dashboard';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: AppUser | null;
  isInitializing: boolean;
  signIn: (credentials: { email: string; password: string }) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  renewSession: () => Promise<AppUser | null>;
  setUser: (nextUser: AppUser | null) => void;
  hasRole: (...roles: readonly UserRole[]) => boolean;
  requestPasswordReset: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
  initialUser?: AppUser | null;
}

interface CurrentProfileRow {
  role: UserRole;
  tenant_id: string | null;
  display_name: string | null;
}

const validRoles = new Set<UserRole>(Object.values(USER_ROLES));

function isCurrentProfileRow(value: unknown): value is CurrentProfileRow {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;
  const { role, tenant_id: tenantId, display_name: displayName } = record;

  if (typeof role !== 'string' || !validRoles.has(role as UserRole)) {
    return false;
  }

  const isTenantValid = tenantId === null || typeof tenantId === 'string';
  const isDisplayNameValid = displayName === null || typeof displayName === 'string';

  return isTenantValid && isDisplayNameValid;
}

async function mapSupabaseUser(authUser: User | null): Promise<AppUser | null> {
  if (authUser === null) {
    return null;
  }

  const profileResponse = await supabase.rpc('get_current_profile').single();
  const profileError = profileResponse.error;
  const profileData: unknown = profileResponse.data;

  if (profileError && import.meta.env.DEV) {
    console.error('mapSupabaseUser profile error', profileError);
  }

  let profile: CurrentProfileRow | null = null;
  if (isCurrentProfileRow(profileData)) {
    profile = profileData;
  }

  const { app_metadata: appMetadata } = authUser;
  const inferredRole = (appMetadata.role as UserRole | undefined) ?? USER_ROLES.administrator;
  const role = profile !== null ? profile.role : inferredRole;
  const tenantId = profile !== null ? profile.tenant_id : null;
  const displayName = profile !== null ? profile.display_name : null;

  const mapped: AppUser = {
    id: authUser.id,
    email: authUser.email ?? null,
    role,
    tenantId,
    displayName,
  };

  return mapped;
}

interface AuthLoadingScreenProps {
  isVisible: boolean;
}

function AuthLoadingScreen({ isVisible }: AuthLoadingScreenProps): JSX.Element {
  return (
    <div
      className={
        'pointer-events-none fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-md bg-surface-cream text-text-primary transition-opacity duration-500 ease-out'
      }
      style={{ opacity: isVisible ? 1 : 0 }}
      aria-live="polite"
      role="status"
    >
      <div className="relative flex h-32 w-32 items-center justify-center">
        <div className="absolute inset-0 animate-ping rounded-full border-4 border-brand-primary/20" />
        <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-white shadow-xl">
          <img
            src={logo}
            alt="Strashin Developments"
            className="absolute inset-0 m-auto h-auto max-h-[78%] w-[78%]"
          />
        </div>
      </div>
      <p className="text-sm text-text-primary/70">Preparing your workspace...</p>
    </div>
  );
}

export function AuthProvider({ children, initialUser = null }: AuthProviderProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<AppUser | null>(initialUser);
  const [status, setStatus] = useState<AuthStatus>(initialUser ? 'authenticated' : 'loading');
  const [isInitializing, setIsInitializing] = useState<boolean>(initialUser ? false : false);
  const isMountedRef = useRef<boolean | null>(null);
  const userRef = useRef<AppUser | null>(initialUser ?? null);
  const inflightUserRef = useRef<{ userId: string; promise: Promise<AppUser | null> } | null>(null);

  const setInitializing = useCallback((value: boolean) => {
    if (isMountedRef.current !== true) {
      return;
    }
    setIsInitializing(value);
  }, []);

  const setUserWithStatus = useCallback((nextUser: AppUser | null) => {
    if (isMountedRef.current !== true) {
      return;
    }

    userRef.current = nextUser;
    setUser(nextUser);
    setStatus(nextUser ? 'authenticated' : 'unauthenticated');
  }, []);

  const resolveSession = useCallback(
    async (authUser: User | null, options: { force?: boolean; preferExisting?: boolean } = {}) => {
      const { force = false, preferExisting = false } = options;

      if (!authUser) {
        inflightUserRef.current = null;
        setUserWithStatus(null);
        return null;
      }

      if (!force && userRef.current?.id === authUser.id) {
        return userRef.current;
      }

      if ((preferExisting || !force) && inflightUserRef.current?.userId === authUser.id) {
        return inflightUserRef.current.promise;
      }

      const resolvePromise = (async () => {
        try {
          const mapped = await mapSupabaseUser(authUser);
          setUserWithStatus(mapped);
          return mapped;
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('Failed to resolve session', error);
          }
          setUserWithStatus(null);
          return null;
        } finally {
          if (inflightUserRef.current?.userId === authUser.id) {
            inflightUserRef.current = null;
          }
        }
      })();

      inflightUserRef.current = { userId: authUser.id, promise: resolvePromise };

      return resolvePromise;
    },
    [setUserWithStatus],
  );

  const runWithInitialization = useCallback(
    async (operation: () => Promise<AppUser | null>) => {
      setInitializing(true);
      try {
        return await operation();
      } finally {
        setInitializing(false);
      }
    },
    [setInitializing],
  );

  const renewSession = useCallback(async (): Promise<AppUser | null> => {
    return runWithInitialization(async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        if ('status' in error && error.status === 400) {
          return await resolveSession(null);
        }
        if (import.meta.env.DEV) {
          console.error('Failed to refresh session', error);
        }
        throw error;
      }
      const sessionUser = data.session ? data.session.user : null;
      return await resolveSession(sessionUser, { force: true });
    });
  }, [resolveSession, runWithInitialization]);

  useEffect(() => {
    isMountedRef.current = true;

    if (initialUser) {
      setUserWithStatus(initialUser);
      setInitializing(false);
      return () => {
        isMountedRef.current = false;
      };
    }

    const bootstrap = async () => {
      await runWithInitialization(async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const initialSessionUser = sessionData.session?.user ?? null;

          if (initialSessionUser) {
            try {
              const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
              if (refreshError) {
                if ('status' in refreshError && refreshError.status === 400) {
                  return await resolveSession(null);
                }
                if (import.meta.env.DEV) {
                  console.error('Failed to refresh session during bootstrap', refreshError);
                }
              } else if (refreshed.session?.user) {
                return await resolveSession(refreshed.session.user, { force: true });
              }
            } catch (refreshError) {
              if (import.meta.env.DEV) {
                console.error('Unexpected session refresh failure', refreshError);
              }
            }

            return await resolveSession(initialSessionUser, { force: true });
          }

          return await resolveSession(null);
        } catch (sessionError) {
          if (import.meta.env.DEV) {
            console.error('Failed to bootstrap session', sessionError);
          }
          return await resolveSession(null);
        }
      });
    };

    void bootstrap();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (isMountedRef.current !== true) {
        return;
      }

      const nextUser = session?.user ?? null;

      switch (event) {
        case 'SIGNED_IN': {
          if (nextUser === null) {
            void resolveSession(null);
            return;
          }

          const isSameUser = userRef.current?.id === nextUser.id;
          const isInflight = inflightUserRef.current?.userId === nextUser.id;

          if (isSameUser || isInflight) {
            void resolveSession(nextUser);
            return;
          }

          void runWithInitialization(async () => {
            return await resolveSession(nextUser, { force: true });
          });
          return;
        }
        case 'USER_UPDATED': {
          void resolveSession(nextUser, { force: true });
          return;
        }
        case 'TOKEN_REFRESHED': {
          void resolveSession(nextUser);
          return;
        }
        default: {
          void resolveSession(nextUser);
        }
      }
    });

    return () => {
      isMountedRef.current = false;
      listener.subscription.unsubscribe();
    };
  }, [initialUser, resolveSession, runWithInitialization, setInitializing, setUserWithStatus]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!location.pathname.startsWith(DASHBOARD_PATH)) {
      navigate(DASHBOARD_PATH, { replace: true });
    }
  }, [location.pathname, navigate, user]);

  const signIn = useCallback(
    async (credentials: { email: string; password: string }) => {
      let outcome: SignInResult = { status: 'error', reason: 'unknown' };

      await runWithInitialization(async () => {
        const result = await signInWithPassword(credentials);
        outcome = result;

        if (result.status === 'success') {
          try {
            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
            if (sessionError) {
              throw sessionError;
            }

            let resolvedSession = sessionData.session;
            if (!resolvedSession && result.session) {
              const { data: setSessionData, error: setSessionError } =
                await supabase.auth.setSession({
                  access_token: result.session.access_token,
                  refresh_token: result.session.refresh_token,
                });
              if (setSessionError) {
                throw setSessionError;
              }
              resolvedSession = setSessionData.session ?? null;
            }

            if (!resolvedSession) {
              outcome = { status: 'error', reason: 'unknown' };
              return null;
            }

            const resolvedUser = await resolveSession(result.user, {
              force: true,
              preferExisting: true,
            });
            if (!resolvedUser) {
              outcome = { status: 'error', reason: 'unknown' };
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              console.error('Failed to resolve session after sign in', error);
            }
            outcome = { status: 'error', reason: 'unknown' };
          }
        }

        return null;
      });

      return outcome;
    },
    [resolveSession, runWithInitialization],
  );

  const signOut = useCallback(async () => {
    await runWithInitialization(async () => {
      await supabase.auth.signOut();
      await resolveSession(null);
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
      return null;
    });
  }, [location.pathname, navigate, resolveSession, runWithInitialization]);

  const requestPasswordReset = useCallback(async (email: string) => {
    const redirectTo =
      typeof window !== 'undefined' ? `${window.location.origin}/auth/update-password` : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) {
      throw error;
    }
  }, []);

  const hasRole = useCallback(
    (...roles: readonly UserRole[]) => userHasRole(user, ...roles),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isInitializing,
      signIn,
      signOut,
      renewSession,
      setUser: setUserWithStatus,
      hasRole,
      requestPasswordReset,
    }),
    [
      hasRole,
      isInitializing,
      renewSession,
      requestPasswordReset,
      setUserWithStatus,
      signIn,
      signOut,
      status,
      user,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthLoadingScreen isVisible={isInitializing} />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
